import { ProfileStatusEnum } from '@zaoblako/shared/modules/security/enums/profile-status.enum';
import { SecurityDtoKeysEnum } from '@zaoblako/shared/modules/security/enums/security-dto-keys.enum';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { FileFindDto, type FileFindInterface } from '@zaoblako/shared/modules/storage/dtos/file/file-find.dto';
import { TelegramChatDto, type TelegramChatInterface } from '@zaoblako/shared/modules/integration/dtos/telegram-chat.dto';

import * as yup from 'yup';

/** Profile response body */
export interface ProfileFindInterface {
  /** Unique identifier. Cannot be changed. */
  id: string;
  /** Profile creation date and time */
  created: Date;
  /** Profile last update date and time */
  updated: Date;
  /** Profile soft deletion date and time (`null` for not deleted profiles) */
  deleted: Date;
  /** Profile status ({@link ProfileStatusEnum.INACTIVE} by default) */
  status?: ProfileStatusEnum;
  /** User email address (login) */
  email: string;
  /** User's phone number */
  phone: string;
  /** User's Google email address (for Google integration) */
  gmail?: string;
  /** User's first (personal) name */
  firstName: string;
  /** User's middle (additional) name or surname */
  middleName: string;
  /** User's last (family) name */
  lastName: string;
  /** User's full name. This value will be generated automatically, using values from other name columns. */
  name: string;
  /** User's last visit date and time */
  lastTime: Date;
  /** Super-admin profile flag. Profiles with this flag has higher access level to some APIs. */
  isSuperAdmin?: boolean;
  /** User's avatar */
  file?: FileFindInterface;
  /** If `true` then entity cards should be opened in new tab */
  cardOnNewTab?: boolean;
  /** Invitation link to the Telegram bot for the current instance of the FRM */
  currentTelegramInviteLink?: string;
  /** Active Telegram chat for the current FRM instance */
  currentTelegramChat?: TelegramChatInterface;
  /** Invitation links to the Telegram bots */
  telegramInviteLinks?: string[];
  /** Active Telegram chats */
  telegramChats?: TelegramChatInterface[];
}

/** Profile response body - DTO schema */
export const ProfileFindDto = SerdeDtoClass.create<ProfileFindInterface>(SecurityDtoKeysEnum.PROFILE_FIND_DTO, {
  id: {
    label: 'Номер',
    hint: 'UUID седьмой версии',
    serializer: yup.string(),
  },
  created: {
    label: 'Дата создания',
    serializer: yup.date(),
  },
  updated: {
    label: 'Дата последнего изменения',
    serializer: yup.date(),
  },
  deleted: {
    label: 'Дата удаления',
    serializer: yup.date(),
  },
  status: {
    optional: true,
    default: ProfileStatusEnum.INACTIVE,
    label: 'Статус',
    serializer: yup
      .string()
      .oneOf(Object.values(ProfileStatusEnum)),
  },
  email: {
    sortable: true,
    label: 'Email',
    serializer: yup.string(),
  },
  phone: {
    sortable: true,
    label: 'Телефон',
    serializer: yup.string(),
  },
  gmail: {
    optional: true,
    label: 'Аккаунт в Google',
    serializer: yup.string(),
  },
  firstName: {
    label: 'Имя',
    serializer: yup.string(),
  },
  middleName: {
    label: 'Отчество',
    serializer: yup.string(),
  },
  lastName: {
    label: 'Фамилия',
    serializer: yup.string(),
  },
  name: {
    sortable: true,
    label: 'ФИО',
    serializer: yup.string(),
  },
  lastTime: {
    label: 'Последняя активность',
    serializer: yup.date(),
  },
  isSuperAdmin: {
    optional: true,
    label: 'Суперпользователь',
    hint: 'Флаг со специальными полномочиями. Только для технических специалистов ФРМ.',
    serializer: yup.boolean(),
  },
  file: {
    optional: true,
    label: 'Аватар',
    nested: FileFindDto,
    serializer: (_, nested) => nested,
  },
  cardOnNewTab: {
    optional: true,
    label: 'Открывать карточку в новой странице',
    serializer: yup.boolean(),
  },
  currentTelegramInviteLink: {
    optional: true,
    label: 'Ссылка-приглашение в Telegram-бота для текущего инстанса ФРМ',
    serializer: yup.string(),
  },
  currentTelegramChat: {
    optional: true,
    nested: TelegramChatDto,
    label: 'Активный чат в Telegram для текущего инстанса ФРМ',
    serializer: (_, nested) => nested,
  },
  telegramInviteLinks: {
    optional: true,
    label: 'Ссылка-приглашения в Telegram-ботоы',
    serializer: yup.array(yup.string()),
  },
  telegramChats: {
    optional: true,
    nested: TelegramChatDto,
    label: 'Активные чаты в Telegram',
    serializer: (_, nested) => yup.array(nested),
  },
});
